/**
 * TanStack Query hooks over the api/resources seam. Pages call these instead of
 * importing mock consts, so the mock→real flip is invisible to the component.
 */
import { useQuery } from '@tanstack/react-query';
import { getDashboard, listBusinesses, listInvoices, getInvoiceDetail, getSubscription, getSubscriptionsMap, getAvailablePlans, getTeam, getPosUserRoles, getPosLocations, getAddOns, getStaffContext } from './resources';
import { getSignupAvailability } from './auth';
import { useSession } from '../stores/session';
import { dhakaToday } from '../lib/format';

export const queryKeys = {
  dashboard: (businessId: string) => ['dashboard', businessId] as const,
  businesses: () => ['businesses'] as const,
  invoices: (businessId?: string) => ['invoices', businessId ?? 'all'] as const,
  subscription: (businessId: string) => ['subscription', businessId] as const,
};

export function useDashboard(businessId: string) {
  return useQuery({
    queryKey: queryKeys.dashboard(businessId),
    queryFn: () => getDashboard(businessId),
    enabled: !!businessId,
  });
}

export function useBusinesses() {
  return useQuery({ queryKey: queryKeys.businesses(), queryFn: () => listBusinesses() });
}

export function useInvoices(businessId?: string) {
  return useQuery({ queryKey: queryKeys.invoices(businessId), queryFn: () => listInvoices(businessId) });
}

export function useInvoice(invoiceId: string) {
  return useQuery({
    queryKey: ['invoice', invoiceId] as const,
    queryFn: () => getInvoiceDetail(invoiceId),
    enabled: !!invoiceId,
  });
}

export function useSubscription(businessId: string) {
  return useQuery({
    queryKey: queryKeys.subscription(businessId),
    queryFn: () => getSubscription(businessId),
    enabled: !!businessId,
  });
}

export function useSubscriptionsMap() {
  return useQuery({ queryKey: ['subscriptions', 'map'], queryFn: () => getSubscriptionsMap() });
}

export function useAvailablePlans(businessId: string) {
  return useQuery({ queryKey: ['availablePlans', businessId], queryFn: () => getAvailablePlans(businessId), enabled: !!businessId });
}

export function useTeam(businessId: string) {
  return useQuery({ queryKey: ['team', businessId], queryFn: () => getTeam(businessId), enabled: !!businessId });
}

/** Live POS roles for a business's members (admin/reseller Users tab). */
export function usePosLocations(businessId: string) {
  return useQuery({ queryKey: ['posLocations', businessId], queryFn: () => getPosLocations(businessId), enabled: !!businessId, staleTime: 30_000 });
}

export function usePosUserRoles(businessId: string) {
  return useQuery({ queryKey: ['posUserRoles', businessId], queryFn: () => getPosUserRoles(businessId), enabled: !!businessId, staleTime: 30_000 });
}

export function useAddOns(businessId: string) {
  return useQuery({ queryKey: ['addons', businessId], queryFn: () => getAddOns(businessId), enabled: !!businessId });
}

export function useStaffContext() {
  return useQuery({ queryKey: ['staff', 'context'], queryFn: () => getStaffContext() });
}

/** Public signup-door availability (direct signup + reseller applications). */
export function useSignupAvailability() {
  return useQuery({ queryKey: ['signup-availability'], queryFn: getSignupAvailability });
}

/** The session's active business, resolved from the businesses list (falls back to the first). */
export function useActiveBusiness() {
  const activeBusinessId = useSession((s) => s.activeBusinessId);
  const q = useBusinesses();
  const data = q.data?.find((b) => b.id === activeBusinessId) ?? q.data?.[0] ?? null;
  return { data, isLoading: q.isLoading };
}

/**
 * POS-door gate derived from the subscription. `blocked` = hide Open POS + show
 * the Pay-now banner (trial lapsed / suspended / canceled). `pastDue` = warn but
 * keep access — dunning's grace window (suspension lands at day 8). The server
 * enforces the same rule in POST /pos/handoff, so this is UX, not security.
 */
export function usePosAccess(businessId: string | null | undefined) {
  const q = useSubscription(businessId ?? '');
  const sub = q.data;
  // Dhaka's calendar day, not UTC's. This is a GATE — it decides whether a trial has
  // ended and therefore whether POS access is cut — and `toISOString()` is UTC, so between
  // 00:00 and 06:00 Dhaka it read as YESTERDAY and a trial that expired today stayed
  // "not yet ended" for those six hours. Same class as the 2026-08-01 date-filter fix
  // (#11): slicing a date off a UTC timestamp misfiles everything created 00:00-06:00.
  const todayIso = dhakaToday();
  const trialEnd = sub?.trialEndsAt ?? sub?.renewsAt ?? null;
  const trialEnded = sub?.status === 'trial' && !!trialEnd && trialEnd.slice(0, 10) < todayIso;
  const blocked = !!sub && (sub.status === 'suspended' || sub.status === 'canceled' || trialEnded);
  return {
    loading: q.isLoading,
    blocked,
    pastDue: sub?.status === 'past_due',
    reason: !blocked ? null : trialEnded ? ('trial_ended' as const) : ('inactive' as const),
    /** Server-computed dunning facts (amount, due date, the exact suspend/cancel dates) so the
     *  banner can state what happens and when, instead of a vague "pay now". */
    dunning: sub?.dunning ?? null,
  };
}
